import { Component, Input, OnInit } from '@angular/core';
import MonColors from '@monsido/colors/dist/es6/colors';
import { PipeService } from '@client/app/filters/pipe-transformation.service';
import { TranslateService } from '@client/app/services/translate/translate.service';
import { AccessibilityResultTypeService } from '@client/app/blocks/service/accessibility/result-type.service';
import { DateTimeService } from '@client/app/services/date-time/date-time.service';
import { safeClone } from '@client/app/shared/utils/object-utils';
import {
    AccessibilityGuidelineVersionService,
} from '@monsido/modules/accessibility/services/accessibility-guideline-version.service';

@Component({
    selector: 'accessibility-dashboard-compliance-bar',
    standalone: false,
    template: `
        <div class="d-flex align-items-baseline accessibility-dashboard-compliance-bar">
            <div class="mr-auto pb-4">
                <div class="text-grey font-weight-500">
                    <span>
                        {{ 'History (last [[historyScans]] scans)' | translate:'':{historyScans} }}
                    </span>
                    <span class="fas fa-question-circle"
                          [triggers]="'focus hover'"
                          [ngbTooltip]="failingAccessibilityMessage">
                    </span>
                </div>
            </div>
            <div class="text-right">
                <p class="mb-1 percentage-block"
                   tabindex="0"
                   [ngClass]="{
                   'text-danger': changeCount > 0,
                    'text-success': changeCount < 0,
                    'text-grey': changeCount == 0
                   }">
                    <span class="fas font-size-0-7"
                          aria-hidden="true"
                          [ngClass]="{
                              'fa-long-arrow-down': changeCount > 0,
                              'fa-long-arrow-up': changeCount < 0
                          }">
                    </span>
                    {{ changeInPercentage }}
                </p>
                <p class="text-grey inline-block mb-0 percentage-status">
                    <span *ngIf="changeCount > 0">
                        +
                    </span>
                    <span *ngIf="changeCount !== 0">
                        {{ changeCount }}
                    </span>
                    <span *ngIf="changeCount > 1" translate>
                        Failing checks found
                    </span>
                    <span *ngIf="changeCount === 1" translate>
                        Failing check found
                    </span>
                    <span *ngIf="changeCount == 0" translate>
                        No changes
                    </span>
                    <span *ngIf="changeCount === -1" translate>
                        Failing check
                    </span>
                    <span *ngIf="changeCount < -1" translate>
                        Failing checks
                    </span>
                </p>
            </div>
        </div>
        <mon-chart-line height="110"
                    [emptyDataMessage]="getEmptyDataMessage()"
                    [options]="barChartOptions"
                    [labels]="barChartLabels"
                    [data]="barChart"
                    [series]="barChartSeries"
                    [colors]="chartColors"
                    [override]="barOverrides">
        </mon-chart-line>
    `,
})
export class AccessibilityDashboardComplianceBarComponent implements OnInit {
    @Input() history: any;
    @Input() type: any;
    @Input() checks: any;
    @Input() selectedObs: any;

    showChangeCount = false;
    changeInPercentage = 0;
    changeCount = 0;
    historyScans = 0;
    barChartOptions: any = {};
    barChartSeries: any = [];
    barOverrides: any = [];
    barChartLabels: any = [];
    barChart: any = [];
    chartColors: any = null;
    failingAccessibilityMessage = 'Failing accessibility checks gives you information about the current ' +
        'Accessibility status of the website/group. It may vary from the history if there have been ' +
        'changes in ignored and review of accessibility issues';

    constructor (
        private ng2PipeService: PipeService,
        private translateService: TranslateService,
        private ng2AccessibilityResultTypeService: AccessibilityResultTypeService,
        private ng2DateTimeService: DateTimeService,
        private ng2AccessibilityGuidelineVersionService: AccessibilityGuidelineVersionService,
    ) {}

    ngOnInit (): void {
        this.failingAccessibilityMessage = this.translateService.getString('Failing accessibility checks gives you information about the current Accessibility status of the website/group. It may vary from the history if there have been changes in ignored and review of accessibility issues');
        this.showChangeCount = false;
        this.changeInPercentage = 0;
        this.changeCount = 0;

        this.barChartOptions = {};
        this.barChartSeries = [];
        this.barOverrides = [];
        this.barChartLabels = [];
        this.barChart = [];

        this.selectedObs.subscribe((resultType: any) => {
            this.setupBarChart(resultType);
            this.historyChanges(resultType);
            this.historyScans = this.history.length;
        });
    }

    setupBarChart (resultType: any): void {
        const history = this.history.filter((crawl: any) => {
            return crawl.accessibility_checks.hasOwnProperty(this.type);
        });
        const complianceSize = this.ng2AccessibilityResultTypeService.getChecksCount(
            this.checks,
            resultType,
            [this.type],
        );
        this.barChartSeries = [
            this.translateService.getString('In compliance'),
            this.translateService.getString('Failing checks'),
        ];
        this.barOverrides = [
            {
                type: 'bar',
                borderWidth: 0,
                backgroundColor: '#57b2b7',
                borderColor: '#57b2b7',
            },
            {
                type: 'bar',
                borderWidth: 0,
                backgroundColor: '#eee',
                borderColor: '#eee',
            },
        ];
        this.barChartLabels = history.map((crawl: any) => {
            return this.ng2DateTimeService.format(crawl.date, 'MMM DD');
        });
        this.barChart = [
            history.map((crawl: any) => {
                const result =
        complianceSize -
        this.ng2AccessibilityResultTypeService.getCrawlIssueCount(
            crawl,
            this.type,
            resultType,
        );
                if (result > 0) {
                    return result;
                } else {
                    return 0;
                }
            }),
            history.map((crawl: any) => {
                const result = this.ng2AccessibilityResultTypeService.getCrawlIssueCount(
                    crawl,
                    this.type,
                    resultType,
                );
                if (result > 0) {
                    return result;
                } else {
                    return 0;
                }
            }),
        ];
        this.barChartOptions = {
            scales: {
                xAxes: [
                    {
                        id: 'x-axis-0',
                        barPercentage: 1,
                        categoryPercentage: 0.5,
                        type: 'category',
                        offset: true,
                        gridLines: {
                            offsetGridLines: true,
                            display: false,
                        },
                        stacked: true,
                    },
                ],
                yAxes: [
                    {
                        id: 'y-axis-0',
                        ticks: {
                            min: 0,
                            max: Math.ceil(complianceSize / 4) * 4,
                            maxTicksLimit: 4,
                            beginAtZero: true,
                            fixedStepSize: Math.ceil(complianceSize / 4),
                        },
                        stacked: true,
                    },
                    {
                        id: 'y-axis-1',
                        display: false,
                        position: 'right',
                        ticks: {
                            beginAtZero: true,
                            min: 0,
                            max: Math.ceil(complianceSize / 4) * 4,
                            fixedStepSize: Math.ceil(complianceSize / 4),
                            maxTicksLimit: 4,
                        },
                        stacked: true,
                    },
                ],
            },
            legend: {
                display: false,
                position: 'bottom',
            },
            tooltips: {
                intersect: false,
                itemSort: (a: any, b: any) => {
                    return b.datasetIndex - a.datasetIndex;
                },
            },
        };
        this.ng2AccessibilityGuidelineVersionService.getVersions(history).then((
            versions: any,
        ) => {
            if (!versions?.length) {
                return;
            }
            const barChartOptions = safeClone(this.barChartOptions);
            barChartOptions.verticalLines = {
                lines: versions.map((version: any) => {
                    return {
                        xAxisID: 'x-axis-0',
                        x: version.time,
                        style: {
                            lineDash: [5, 5],
                            color: MonColors['secondary-15'],
                            width: 3,
                        },
                        label: {
                            text: version.title,
                            color: MonColors['secondary-15'],
                            position: 'top',
                        },
                        data: {
                            link: version.infoLink,
                        },
                    };
                }),
                timeScale: history.map((crawl: any) => {
                    return crawl.post_processing_done_at;
                }),
            };
            this.barChartOptions = barChartOptions;
        });
    }

    historyChanges (resultType: any): void {
        // Changes between the first scan and live counter
        if (this.history.length > 1) {
            const latestError = this.ng2AccessibilityResultTypeService.getChecksCount(
                this.checks,
                resultType,
                [this.type],
                true,
            );
            const firstError = this.history[0].accessibility_checks[this.type] || 0;
            const change = latestError - firstError;
            const changeInPlus = Math.abs(change);
            const changeInPercentage = this.neverExceedHundredPercentage(
                (changeInPlus / firstError) * 100 || 0,
            ); // If division is NaN, set 0

            this.changeCount = change;
            this.changeInPercentage = this.ng2PipeService.get('monNumber')(
                changeInPercentage / 100,
                '0.[0] %',
            );
        }
    }

    neverExceedHundredPercentage (percentage: number): number {
        if (percentage > 100) {
            return 100;
        }
        return percentage;
    }

    getEmptyDataMessage (): string {
        const params = {
            lvl: this.type,
        };
        return this.translateService.getString(
            'No Historic data available for Accessibility Level [[lvl]]',
            params,
        );
    }
}
